/**
 * Remote-cancellation control for a CLI-driven eval session.
 *
 * A session lives on the backend, not in this process: killing the CLI leaves it
 * running. Re-running `run-eval` then overlaps the abandoned session against the
 * same shared fixture, which corrupts both runs' data (gh #3708). So an
 * interrupted `run-eval` must cancel the session it started, or say plainly that
 * it could not and how to finish the job by hand.
 */
import type { StopSessionResponse, SeedReleaseStatus } from '@wayai/core/contracts';
import { ApiError } from './api-client.js';
import { extractApiMessage } from './errors.js';

/** 128 + signal number, the conventional shell exit code for a signalled process. */
const SIGNAL_EXIT_CODE = { SIGINT: 130, SIGTERM: 143, SIGHUP: 129 } as const;
type HandledSignal = keyof typeof SIGNAL_EXIT_CODE;
const HANDLED_SIGNALS = Object.keys(SIGNAL_EXIT_CODE) as HandledSignal[];

const STOP_ATTEMPTS = 3;
const STOP_ATTEMPT_TIMEOUT_MS = 10_000;
/** Gaps between attempts, mirroring `RETRYABLE_BACKOFF_MS` in api-client.ts. */
const STOP_BACKOFF_MS = [500, 1000] as const;

/** The exact command that cancels this session by hand. Printed, never executed. */
export function formatEvalSessionStopCommand(sessionId: string, hubId: string): string {
  return `wayai eval session stop ${sessionId} --hub ${hubId}`;
}

/**
 * The command that shows a finished session's scores. `--hub` is explicit
 * because `eval-results` resolves its own hub: without it the line fails
 * outright in a multi-hub workspace, and silently targets the wrong hub in a
 * single-hub one — leaving a re-run as the user's only obvious move.
 */
export function formatEvalResultsCommand(sessionId: string, hubId: string): string {
  return `wayai eval-results --session ${sessionId} --hub ${hubId}`;
}

export type StopOutcome =
  | { kind: 'cancelled'; seedRelease: SeedReleaseStatus }
  | { kind: 'already_finished'; status: string };

/**
 * The line to print after "cancellation requested", chosen by what the backend
 * actually did with the fixture (gh #3745):
 *   • `pending`        — turns admitted before the stop are still running, so the
 *                        fixture stays reserved. Starting a replacement run now
 *                        would 409 on it.
 *   • `releasing`      — the session was quiescent, so nothing is still writing
 *                        and the release is under way. Deliberately not phrased
 *                        as done: it completes just after the response.
 *   • `not_applicable` — no lease at all (unseeded, or a pre-lease session).
 *                        Quiescence was never checked, so keep the old caution.
 */
export function describeSeedRelease(status: SeedReleaseStatus): string {
  if (status === 'pending') {
    return 'A turn that started before the stop is still finishing — its fixture stays reserved until that turn settles.';
  }
  if (status === 'releasing') return 'Its fixture is being released.';
  return 'A turn already in progress may still be finishing — wait for it before starting another run.';
}

/**
 * What the backend actually did. `stopped` is the authoritative signal — status
 * alone can't tell a fresh cancel from a repeat one, since both read
 * `cancelled`. Reporting a cancel that didn't happen sends the user to re-run a
 * scored session, re-mutating the shared fixture (gh #3708).
 *
 * `seedRelease` carries the other half of the truth (gh #3745): a stop does not
 * halt a turn that was already claimed, so the fixture can stay leased until
 * those turns drain. Saying "stopped" alone would invite the user to immediately
 * launch a replacement run that then 409s on the fixture they think they freed.
 */
export function classifyStopOutcome(response: StopSessionResponse): StopOutcome {
  return response.data.stopped
    ? { kind: 'cancelled', seedRelease: response.data.seed_release_status }
    : { kind: 'already_finished', status: response.data.session.session_status };
}

/**
 * A status that will not change on retry: the request was understood and
 * refused. Retrying a 404 (no such session) or a 401 (credentials rejected)
 * re-pays the client's token refresh for the same answer.
 */
function isTerminalStopFailure(err: unknown): boolean {
  return err instanceof ApiError
    && err.status >= 400 && err.status < 500
    && err.status !== 408 && err.status !== 429;
}

/**
 * Stop with bounded, spaced retries. Each attempt is capped so a hung request
 * can't trap a user who just pressed Ctrl-C, and the gaps exist because the
 * failures worth retrying (a wifi handoff, an edge 502) reject in milliseconds —
 * three back-to-back attempts would all land inside the same blip.
 */
export async function stopEvalSessionWithRetries(
  stop: () => Promise<StopSessionResponse>,
  opts: {
    attempts?: number;
    attemptTimeoutMs?: number;
    backoffMs?: readonly number[];
    sleep?: (ms: number) => Promise<void>;
  } = {},
): Promise<StopSessionResponse> {
  const attempts = opts.attempts ?? STOP_ATTEMPTS;
  const attemptTimeoutMs = opts.attemptTimeoutMs ?? STOP_ATTEMPT_TIMEOUT_MS;
  const backoffMs = opts.backoffMs ?? STOP_BACKOFF_MS;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));

  let lastError: unknown = new Error('Stop was never attempted');
  for (let attempt = 1; attempt <= attempts; attempt++) {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        stop(),
        new Promise<never>((_resolve, reject) => {
          timeout = setTimeout(
            () => reject(new Error(`Stop attempt ${attempt} timed out after ${attemptTimeoutMs}ms`)),
            attemptTimeoutMs,
          );
        }),
      ]);
    } catch (err) {
      lastError = err;
      if (isTerminalStopFailure(err)) break;
    } finally {
      if (timeout !== undefined) clearTimeout(timeout);
    }
    const delay = backoffMs[attempt - 1];
    if (attempt < attempts && delay) await sleep(delay);
  }
  throw lastError;
}

export interface EvalSignalController {
  /** The manual recovery command for this session — the caller's single source for it. */
  readonly manualStopCommand: string;
  /**
   * False immediately when no signal has arrived. When one has, resolves once
   * handling finishes (confirmed stop, or given up) and returns true — at which
   * point the caller must return and let the handler own the exit.
   */
  interrupted(): Promise<boolean>;
  dispose(): void;
}

/**
 * Install remote-cancellation handlers. Call once the remote session exists —
 * before that there is nothing to cancel, and after that every phase is
 * interruptible.
 *
 * The first signal waits for a confirmed backend stop rather than exiting
 * immediately: leaving silently is the bug. A second signal honors the user's
 * intent to leave now and prints the exact recovery command instead.
 *
 * SIGHUP is handled alongside SIGINT/SIGTERM because closing a terminal or
 * dropping an SSH session sends it, and `run-eval` holds the foreground for up
 * to its full timeout — abandoning the session there is the same defect.
 */
export function installEvalSignalHandlers(input: {
  hubId: string;
  sessionId: string;
  stop: () => Promise<StopSessionResponse>;
  /** Test seam — defaults to `process.exit`. */
  exit?: (code: number) => void;
}): EvalSignalController {
  const exit = input.exit ?? ((code: number) => process.exit(code));
  const manualStopCommand = formatEvalSessionStopCommand(input.sessionId, input.hubId);
  let signalCount = 0;
  let disposed = false;
  let settle!: () => void;
  const settled = new Promise<void>((resolve) => { settle = resolve; });
  const listeners = new Map<HandledSignal, () => void>();

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    for (const [signal, listener] of listeners) process.off(signal, listener);
  };

  const finish = (exitCode: number) => {
    dispose();
    settle();
    exit(exitCode);
  };

  const handle = (signal: HandledSignal) => {
    signalCount += 1;
    const signalExitCode = SIGNAL_EXIT_CODE[signal];

    if (signalCount > 1) {
      console.error('\nSecond signal received; exiting immediately. The remote eval session may still be running.');
      console.error(`Run: ${manualStopCommand}`);
      finish(signalExitCode);
      return;
    }

    console.error(`\n${signal} received; stopping remote eval session ${input.sessionId}...`);
    void stopEvalSessionWithRetries(input.stop).then((response) => {
      // A second signal already reported and exited — don't contradict it.
      if (signalCount > 1) return;
      const outcome = classifyStopOutcome(response);
      if (outcome.kind === 'already_finished') {
        // The run finished in the poll gap. Say so and point at its scores —
        // "cancelled" here would send the user to re-run a session that is done.
        console.error(`Session had already finished (${outcome.status}) — nothing was cancelled.`);
        console.error(`See its results: ${formatEvalResultsCommand(input.sessionId, input.hubId)}`);
      } else {
        // "Requested", not "done": a turn ConversationDO already admitted keeps
        // running — its schedule row is deleted at claim time, so nothing can
        // cancel it. Claiming the session is stopped would invite an immediate
        // replacement run that overlaps it on the fixture (report a5a0bf58).
        console.error('Remote session cancellation requested.');
        console.error(describeSeedRelease(outcome.seedRelease));
      }
      finish(signalExitCode);
    }).catch((err) => {
      if (signalCount > 1) return;
      console.error(`Failed to confirm remote cancellation: ${extractApiMessage(err)}`);
      console.error(`The session may still be running. Run: ${manualStopCommand}`);
      // Not the signal's exit code: the user asked to cancel and we could not.
      finish(1);
    });
  };

  for (const signal of HANDLED_SIGNALS) {
    const listener = () => handle(signal);
    listeners.set(signal, listener);
    process.on(signal, listener);
  }

  return {
    manualStopCommand,
    async interrupted() {
      if (signalCount === 0) return false;
      await settled;
      return true;
    },
    dispose,
  };
}
